import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import * as moment from 'moment';

export interface SalesReadingData {
  gross_sales: number;
  vatable_sales: number;
  vat_exempt_sales: number;
  vat_amount: number;
  total_discounts: number;
  total_refunds: number;
  total_cancelled: number;
  net_sales: number;
  cumulative_grand_total: number;
  closing_date_time: Date;
  generated_by: string;
  reading_number: string;
  bir_permit_number?: string;
  tin_number?: string;
  reading_type: 'Z' | 'X' | 'DSR' | 'CUMULATIVE' | 'HOURLY' | 'CANCELLED';
  is_final: boolean;
}

export interface HourlyData {
  hour: number;
  time_range: string;
  transactions: number;
  revenue: number;
  average_transaction_value: number;
}

export interface CancelledTransaction {
  transaction_id: string;
  plate_number: string;
  original_amount: number;
  cancelled_amount: number;
  cancellation_reason: string;
  cancelled_by: string;
  cancellation_date: Date;
  approval_reference?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SalesReadingsService {

  constructor(private firestore: AngularFirestore) { }

  /**
   * Get logs for specific date range with optimized query and comprehensive filtering
   * @param accountKey - Account identifier
   * @param startDate - Start of date range (inclusive)
   * @param endDate - End of date range (inclusive)
   * @param zone - Zone ID or 'all' for all zones
   * @param employee - Employee ID or 'all' for all employees
   * @returns Filtered transaction logs
   */
  async getLogsForDateRange(accountKey: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<any[]> {
    try {
      let query;

      // Apply zone filter at database level for better performance
      if (zone && zone.trim() !== '' && zone !== 'all') {
        console.log(`Filtering by zone: ${zone}`);
        query = this.firestore.collection('logs')
          .ref.where('account_key', '==', accountKey)
          .where('zone_id', '==', zone)
          .where('time_in', '>=', startDate)
          .where('time_in', '<=', endDate);
      } else {
        console.log('Fetching all zones');
        query = this.firestore.collection('logs')
          .ref.where('account_key', '==', accountKey)
          .where('time_in', '>=', startDate)
          .where('time_in', '<=', endDate);
      }

      const snapshot = await query.get();
      let logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));

      // Apply employee filter at application level (client-side)
      // This checks multiple payment-related fields to ensure we capture ALL transactions
      // where the selected employee was involved in ANY capacity (payment, processing, refund, cancel)
      if (employee && employee.trim() !== '' && employee !== 'all') {
        console.log(`Filtering by employee: ${employee}`);
        logs = logs.filter(log => {
          // Check all possible employee fields related to transaction handling
          const employeeFields = [
            // Payment-related fields
            log.initial_payment_recieved_by_id,
            log.additional_payment_recieved_by_id,
            log.paidBy_id,
            log.paid_by_id,
            // Processing-related fields
            log.processedBy_id,
            log.processed_by_id,
            log.end_session_by_id,
            log.ended_by_id,
            // Refund-related fields
            log.refunded_by_id,
            log.refund_by_id,
            // Cancel-related fields
            log.cancelled_by_id,
            log.canceled_by_id,
            log.cancel_by_id,
            // Time in/out related
            log.time_in_by_id,
            log.time_out_by_id,
            // Created/Updated by
            log.created_by_id,
            log.updated_by_id
          ];

          // Check if any of the employee fields match the selected employee
          const isMatchingEmployee = employeeFields.some(field => field === employee);

          if (isMatchingEmployee) {
            console.log(`Transaction ${log.plate_number || log.id} matched employee ${employee}`);
          }

          return isMatchingEmployee;
        });
        console.log(`Employee filter result: ${logs.length} transactions for employee ${employee}`);
      }

      console.log(`Total transactions fetched: ${logs.length} (Zone: ${zone || 'all'}, Employee: ${employee || 'all'})`);

      return logs;
    } catch (error) {
      console.error('Error fetching logs for date range:', error);
      return [];
    }
  }

  /**
   * Process Z Reading (End-of-Day) Report
   */
  async processZReadingReport(accountKey: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<SalesReadingData & { total_transactions: number }> {
    const logs = await this.getLogsForDateRange(accountKey, startDate, endDate, zone, employee);

    const grossSales = Math.round(this.calculateGrossSales(logs) * 100) / 100;
    const vatAmount = this.calculateVATAmount(grossSales);
    const vatableSales = Math.round((grossSales - vatAmount) * 100) / 100;
    const vatExemptSales = Math.round(this.calculateVATExemptSales(logs) * 100) / 100;
    const totalDiscounts = Math.round(this.calculateTotalDiscounts(logs) * 100) / 100;
    const totalRefunds = Math.round(this.calculateTotalRefunds(logs) * 100) / 100;
    const totalCancelled = this.calculateCancelledTransactions(logs);
    const netSales = Math.round((grossSales - totalRefunds - totalDiscounts) * 100) / 100;

    return {
      gross_sales: grossSales,
      vatable_sales: vatableSales,
      vat_exempt_sales: vatExemptSales,
      vat_amount: vatAmount,
      total_discounts: totalDiscounts,
      total_refunds: totalRefunds,
      total_cancelled: totalCancelled,
      net_sales: netSales,
      cumulative_grand_total: await this.updateCumulativeTotal(netSales),
      closing_date_time: new Date(),
      generated_by: 'Current User', // Replace with actual user
      reading_number: await this.generateReadingNumber('Z'),
      reading_type: 'Z',
      is_final: true,
      total_transactions: logs.length
    };
  }

  /**
   * Process X Reading (Current Totals) Report
   */
  async processXReadingReport(accountKey: string, zone?: string, employee?: string): Promise<SalesReadingData & { total_transactions: number }> {
    const todayStart = moment().startOf('day').toDate();
    const now = new Date();

    const zReadingData = await this.processZReadingReport(accountKey, todayStart, now, zone, employee);

    return {
      ...zReadingData,
      reading_type: 'X',
      reading_number: await this.generateReadingNumber('X'),
      is_final: false
    };
  }

  /**
   * Process Daily Sales Report (DSR) with BIR-compliant data
   */
  async processDailySalesReport(accountKey: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<any> {
    // Get all logs in a single query
    const logs = await this.getLogsForDateRange(accountKey, startDate, endDate, zone, employee);

    // Process hourly breakdown directly from logs instead of making 24 separate queries
    const hourlyData = this.processHourlyBreakdownFromLogs(logs, startDate, endDate);

    // Calculate BIR-compliant financial data with precise rounding (same as Z/X reading)
    const grossSales = Math.round(this.calculateGrossSales(logs) * 100) / 100;
    const vatAmount = this.calculateVATAmount(grossSales);
    const vatableSales = Math.round((grossSales - vatAmount) * 100) / 100;
    const vatExemptSales = Math.round(this.calculateVATExemptSales(logs) * 100) / 100;
    const totalDiscounts = Math.round(this.calculateTotalDiscounts(logs) * 100) / 100;
    const totalRefunds = Math.round(this.calculateTotalRefunds(logs) * 100) / 100;
    const totalCancelled = this.calculateCancelledTransactions(logs);
    const netSales = Math.round((grossSales - totalRefunds - totalDiscounts) * 100) / 100;

    // Process cancelled transactions (only cancelled, not refunded)
    const cancelledTransactions = logs.filter(log =>
      (log.status === 'cancelled' || log.status === 'canceled' || log.isCanceled === true) &&
      log.status !== 'refunded' && log.isRefunded !== true
    ).map(log => {
      // Use amount_paid as primary source for cancelled amount
      let cancelledAmount = 0;
      if (log.amount_paid !== undefined && log.amount_paid !== null) {
        cancelledAmount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
      }

      // Use original_amount if available, otherwise fallback to initial + additional payment
      let originalAmount = 0;
      if (log.original_amount !== undefined && log.original_amount !== null) {
        originalAmount = !isNaN(parseFloat(log.original_amount)) ? parseFloat(log.original_amount) : 0;
      } else {
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        originalAmount = initialPayment + additionalPayment;
      }

      return {
        transaction_id: log.id,
        plate_number: log.plate_number || 'Unknown',
        original_amount: originalAmount,
        cancelled_amount: cancelledAmount,
        cancellation_reason: log.cancellation_reason || log.remarks || 'Not specified',
        cancelled_by: log.cancelled_by || log.end_session_by || log.paid_by || 'Unknown',
        cancellation_date: log.cancelled_at || log.updatedAt ? new Date((log.cancelled_at || log.updatedAt).seconds * 1000) : new Date(),
        approval_reference: log.reference_number || '',
        ticket_number: log.ticket_number || '',
        vehicle_type: log.vehicle_type_custom_name || log.vehicle_type || 'Unknown',
        zone_name: log.zone_name || 'Unknown'
      };
    });

    // Process refunded transactions (separate from cancelled)
    const refundedTransactions = logs.filter(log =>
      log.status === 'refunded' || log.isRefunded === true
    ).map(log => {
      // Use refund_amount as primary source
      let refundAmount = 0;
      if (log.refund_amount !== undefined && log.refund_amount !== null && parseFloat(log.refund_amount) > 0) {
        refundAmount = !isNaN(parseFloat(log.refund_amount)) ? parseFloat(log.refund_amount) : 0;
      } else if (log.amount_paid !== undefined && log.amount_paid !== null) {
        refundAmount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
      } else {
        // Fallback: use initial_payment + additional_payment
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        refundAmount = initialPayment + additionalPayment;
      }

      // Use original_amount if available, otherwise use amount_paid or payments sum
      let originalAmount = 0;
      if (log.original_amount !== undefined && log.original_amount !== null) {
        originalAmount = !isNaN(parseFloat(log.original_amount)) ? parseFloat(log.original_amount) : 0;
      } else if (log.amount_paid !== undefined && log.amount_paid !== null) {
        originalAmount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
      } else {
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        originalAmount = initialPayment + additionalPayment;
      }

      return {
        transaction_id: log.id,
        plate_number: log.plate_number || 'Unknown',
        original_amount: originalAmount,
        refund_amount: refundAmount,
        refund_reason: log.refund_reason || log.remarks || 'Not specified',
        refunded_by: log.refunded_by || log.end_session_by || log.paid_by || 'Unknown',
        refund_date: log.refunded_at || log.updatedAt ? new Date((log.refunded_at || log.updatedAt).seconds * 1000) : new Date(),
        approval_reference: log.reference_number || '',
        ticket_number: log.ticket_number || '',
        vehicle_type: log.vehicle_type_custom_name || log.vehicle_type || 'Unknown',
        zone_name: log.zone_name || 'Unknown'
      };
    });

    // Calculate totals for cancelled and refunded separately
    const totalCancelledAmount = cancelledTransactions.reduce((sum, t) => sum + t.cancelled_amount, 0);
    const totalRefundedAmount = refundedTransactions.reduce((sum, t) => sum + t.refund_amount, 0);

    return {
      // Transaction details
      transactions: this.getDetailedTransactionList(logs),

      // BIR-compliant financial summary (Z/X reading style)
      gross_sales: grossSales,
      vatable_sales: vatableSales,
      vat_exempt_sales: vatExemptSales,
      vat_amount: vatAmount,
      zero_rated_sales: 0, // Typically 0 for parking systems
      total_discounts: totalDiscounts,
      total_refunds: totalRefunds,
      net_sales: netSales,
      cumulative_grand_total: netSales, // For DSR, use net sales as cumulative

      // Cancelled transactions data (separate from refunds)
      cancelled_transactions: cancelledTransactions,
      total_cancelled: cancelledTransactions.length,
      total_cancelled_amount: totalCancelledAmount,
      cancellation_rate: logs.length > 0 ? (cancelledTransactions.length / logs.length) * 100 : 0,

      // Refunded transactions data (separate from cancellations)
      refunded_transactions: refundedTransactions,
      total_refunded: refundedTransactions.length,
      total_refunded_amount: totalRefundedAmount,
      refund_rate: logs.length > 0 ? (refundedTransactions.length / logs.length) * 100 : 0,

      // Summary data
      payment_summary: this.groupByPaymentType(logs),
      vehicle_type_summary: this.groupByVehicleType(logs),
      zone_summary: this.groupByZone(logs),
      employee_summary: this.groupByEmployee(logs),
      hourly_breakdown: hourlyData,
      total_transactions: logs.length,
      total_revenue: grossSales,

      // Report metadata
      reading_number: await this.generateReadingNumber('DSR'),
      reading_type: 'DSR' as const,
      generated_by: 'System',
      closing_date_time: new Date()
    };
  }

  /**
   * Process hourly breakdown from existing logs (faster than making separate queries)
   */
  private processHourlyBreakdownFromLogs(logs: any[], startDate: Date, endDate: Date): HourlyData[] {
    const hourlyData: HourlyData[] = [];
    const startDay = moment(startDate).startOf('day');
    const endDay = moment(endDate).endOf('day');
    const totalDays = moment(endDay).diff(startDay, 'days') + 1;

    // Only process 24 hours if viewing a single day
    if (totalDays === 1) {
      for (let hour = 0; hour < 24; hour++) {
        const hourStart = moment(startDate).hour(hour).startOf('hour');
        const hourEnd = moment(startDate).hour(hour).endOf('hour');

        // Filter logs for this hour
        const hourLogs = logs.filter(log => {
          const logTime = moment(log.time_in.seconds * 1000);
          return logTime.isSameOrAfter(hourStart) && logTime.isSameOrBefore(hourEnd);
        });

        const revenue = this.calculateGrossSales(hourLogs);

        hourlyData.push({
          hour: hour,
          time_range: `${hourStart.format('HH:mm')} - ${hourEnd.format('HH:mm')}`,
          transactions: hourLogs.length,
          revenue: revenue,
          average_transaction_value: hourLogs.length > 0 ? revenue / hourLogs.length : 0
        });
      }
    } else {
      // For multi-day reports, group by day instead of by hour
      const dailyGroups: { [key: string]: any[] } = {};

      logs.forEach(log => {
        const logDate = moment(log.time_in.seconds * 1000).format('YYYY-MM-DD');
        if (!dailyGroups[logDate]) {
          dailyGroups[logDate] = [];
        }
        dailyGroups[logDate].push(log);
      });

      // Convert daily groups to hourly format (by day)
      Object.keys(dailyGroups).sort().forEach((date, index) => {
        const dayLogs = dailyGroups[date];
        const dayRevenue = this.calculateGrossSales(dayLogs);

        hourlyData.push({
          hour: index,
          time_range: moment(date).format('MMM DD, YYYY'),
          transactions: dayLogs.length,
          revenue: dayRevenue,
          average_transaction_value: dayLogs.length > 0 ? dayRevenue / dayLogs.length : 0
        });
      });
    }

    return hourlyData;
  }

  /**
   * Process Cumulative Sales Reading
   */
  async processCumulativeSalesReport(accountKey: string, fromDate: Date, toDate: Date): Promise<any> {
    const logs = await this.getLogsForDateRange(accountKey, fromDate, toDate);

    return {
      period_summary: {
        total_transactions: logs.length,
        total_revenue: this.calculateGrossSales(logs),
        cumulative_vat: this.calculateVATAmount(this.calculateGrossSales(logs))
      },
      running_totals: {
        lifetime_transactions: await this.getLifetimeTransactionCount(),
        lifetime_revenue: await this.getLifetimeRevenue(),
        system_start_date: new Date('2024-01-01') // Replace with actual start date
      },
      audit_trail: {
        last_z_reading: await this.getLastZReadingInfo(),
        system_integrity: this.validateSystemIntegrity()
      }
    };
  }

  /**
   * Process Hourly Breakdown Report
   */
  async processHourlyBreakdownReport(accountKey: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<any> {
    // Get logs first, then process hourly data locally (more efficient)
    const logs = await this.getLogsForDateRange(accountKey, startDate, endDate, zone, employee);
    const hourlyData = this.processHourlyBreakdownFromLogs(logs, startDate, endDate);

    return {
      hourly_data: hourlyData,
      peak_hours: this.identifyPeakHours(hourlyData),
      low_traffic_hours: this.identifyLowTrafficHours(hourlyData),
      total_daily_revenue: hourlyData.reduce((sum, hour) => sum + hour.revenue, 0),
      total_daily_transactions: hourlyData.reduce((sum, hour) => sum + hour.transactions, 0)
    };
  }

  /**
   * Process Cancelled Transactions Report
   */
  async processCancelledTransactionsReport(accountKey: string, startDate: Date, endDate: Date, zone?: string, employee?: string): Promise<any> {
    const logs = await this.getLogsForDateRange(accountKey, startDate, endDate, zone, employee);

    const cancelledTransactions = logs.filter(log =>
      log.status === 'cancelled' || log.status === 'canceled' || log.status === 'refunded'
    ).map(log => ({
      transaction_id: log.id,
      plate_number: log.plate_number,
      original_amount: parseFloat(log.amount_paid || 0),
      cancelled_amount: parseFloat(log.amount_paid || 0),
      cancellation_reason: log.remarks || 'Not specified',
      cancelled_by: log.end_session_by || log.paid_by || 'Unknown',
      cancellation_date: log.updatedAt ? new Date(log.updatedAt.seconds * 1000) : new Date(),
      approval_reference: log.reference_number
    }));

    return {
      cancelled_transactions: cancelledTransactions,
      total_cancelled_amount: this.calculateTotalCancelledAmount(cancelledTransactions),
      cancellation_rate: this.calculateCancellationRate(cancelledTransactions, logs.length),
      audit_trail: this.generateCancellationAuditTrail(cancelledTransactions),
      total_transactions: logs.length
    };
  }

  // Helper Methods

  private calculateGrossSales(logs: any[]): number {
    // Gross Sales = ALL transactions where money was collected
    // This includes:
    // - Paid transactions (paid === true)
    // - Refunded transactions (money was collected, then returned)
    // - Cancelled transactions that have payment (initial_payment > 0 or amount_paid > 0)
    // They must all be included in Gross Sales for proper BIR audit trail
    return logs.reduce((total, log) => {
      // Check if this transaction has any payment amount
      const hasPayment = (
        (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) ||
        (log.initial_payment !== undefined && log.initial_payment !== null && parseFloat(log.initial_payment) > 0)
      );

      // Include transaction if:
      // 1. It was paid (paid === true)
      // 2. It was refunded (isRefunded === true or status === 'refunded')
      // 3. It was cancelled but has payment (cancelled with payment received)
      const isPaid = log.paid === true;
      const isRefunded = log.isRefunded === true || log.status === 'refunded';
      const isCancelledWithPayment = (log.status === 'cancelled' || log.status === 'canceled' || log.isCanceled === true) && hasPayment;

      if (!isPaid && !isRefunded && !isCancelledWithPayment) {
        return total;
      }

      // Use amount_paid as primary source (actual money collected)
      if (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) {
        const amountPaid = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        return total + amountPaid;
      } else {
        // Fallback: use initial_payment + additional_payment
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        return total + initialPayment + additionalPayment;
      }
    }, 0);
  }

  private calculateVATAmount(grossSales: number): number {
    // VAT is 12% of the net amount (gross / 1.12 * 0.12)
    // Apply precise rounding to 2 decimal places to match BIR standards
    const vatAmount = (grossSales / 1.12) * 0.12;
    return Math.round(vatAmount * 100) / 100;
  }

  private calculateVATExemptSales(logs: any[]): number {
    return logs.reduce((total, log) => {
      // Check if transaction has discount information in remarks or other fields
      if (log.remarks && (log.remarks.includes('Senior') || log.remarks.includes('PWD') || log.remarks.includes('Discount'))) {
        // Use same amount calculation logic as reports
        let amount = 0;
        if (log.amount_paid !== undefined && log.amount_paid !== null) {
          amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        } else if (log.paid === true) {
          const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
          const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
          amount = initialPayment + additionalPayment;
        }
        return total + amount;
      }
      return total;
    }, 0);
  }

  private calculateTotalDiscounts(logs: any[]): number {
    return logs.reduce((total, log) => {
      // Use discount_savings field (actual discount amount) if available
      if (log.discount_savings !== undefined && log.discount_savings !== null) {
        const discountSavings = !isNaN(parseFloat(log.discount_savings)) ? parseFloat(log.discount_savings) : 0;
        return total + discountSavings;
      }

      // Fallback: Check for discount information in discount_details or calculate from payments
      if (log.discount_details || (log.remarks && log.remarks.includes('Discount'))) {
        // Calculate discount as difference between original amount and discounted amount
        if (log.original_amount && log.discounted_amount) {
          const originalAmount = !isNaN(parseFloat(log.original_amount)) ? parseFloat(log.original_amount) : 0;
          const discountedAmount = !isNaN(parseFloat(log.discounted_amount)) ? parseFloat(log.discounted_amount) : 0;
          return total + Math.max(0, originalAmount - discountedAmount);
        }

        // Alternative: calculate based on expected vs actual payment
        const expectedAmount = (log.initial_payment || 0) + (log.additional_payment || 0);
        const actualPaid = log.amount_paid || 0;
        return total + Math.max(0, expectedAmount - actualPaid);
      }

      return total;
    }, 0);
  }

  private calculateTotalRefunds(logs: any[]): number {
    // Refunds = Sum of refund_amount (or amount_paid) where isRefunded=true or status='refunded'
    return logs.reduce((total, log) => {
      // Check if transaction is refunded
      const isRefunded = log.isRefunded === true || log.status === 'refunded';

      if (!isRefunded) {
        return total;
      }

      // Use refund_amount field if available (most accurate)
      if (log.refund_amount !== undefined && log.refund_amount !== null && log.refund_amount > 0) {
        const refundAmount = !isNaN(parseFloat(log.refund_amount)) ? parseFloat(log.refund_amount) : 0;
        return total + refundAmount;
      }

      // Fallback: Use amount_paid as the refund amount
      if (log.amount_paid !== undefined && log.amount_paid !== null) {
        const refundedAmount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        return total + refundedAmount;
      }

      // Last fallback: use initial_payment + additional_payment
      const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
      const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
      return total + initialPayment + additionalPayment;
    }, 0);
  }

  private calculateCancelledTransactions(logs: any[]): number {
    return logs.filter(log => log.status === 'cancelled' || log.status === 'canceled').length;
  }

  private async updateCumulativeTotal(netSales: number): Promise<number> {
    // In a real implementation, this would update and return cumulative total from database
    return netSales; // Simplified for now
  }

  private async generateReadingNumber(type: 'Z' | 'X' | 'DSR'): Promise<string> {
    const date = moment().format('YYYYMMDD');
    const timestamp = moment().format('HHmmss');
    return `${type}-${date}-${timestamp}`;
  }

  private getDetailedTransactionList(logs: any[]): any[] {
    return logs.map(log => {
      // Check if this transaction has any payment amount
      const hasPayment = (
        (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) ||
        (log.initial_payment !== undefined && log.initial_payment !== null && parseFloat(log.initial_payment) > 0)
      );

      // Include transaction if:
      // 1. It was paid (paid === true)
      // 2. It was refunded (isRefunded === true or status === 'refunded')
      // 3. It was cancelled but has payment
      const isPaid = log.paid === true;
      const isRefunded = log.isRefunded === true || log.status === 'refunded';
      const isCancelled = log.status === 'cancelled' || log.status === 'canceled' || log.isCanceled === true;
      const hasTransaction = isPaid || isRefunded || (isCancelled && hasPayment) || hasPayment;

      // Calculate amount using same logic as calculateGrossSales
      let amount = 0;
      if (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) {
        amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
      } else if (hasTransaction) {
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        amount = initialPayment + additionalPayment;
      }

      return {
        invoice_number: log.invoice_number || log.reference_number || log.ticket_number || '',
        plate_number: log.plate_number || 'Unknown',
        vehicle_type: log.vehicle_type_custom_name || 'Unknown',
        payment_type: log.payment_type || 'Cash',
        time_in: log.time_in?.seconds ? moment(log.time_in.seconds * 1000).format('MM/DD/YYYY hh:mm A') : (log.time_in || 'Unknown'),
        time_out: log.time_out?.seconds ? moment(log.time_out.seconds * 1000).format('MM/DD/YYYY hh:mm A') : (log.time_out || 'Active'),
        amount: Math.round(amount * 100) / 100, // Apply precise rounding

        // Zone information
        zone_id: log.zone_id || 'unknown',
        zone_name: log.zone_name || 'Unknown Zone',

        // Employee information - who received initial payment
        initial_payment_recieved_by_id: log.initial_payment_recieved_by_id || null,
        initial_payment_recieved_by: log.initial_payment_recieved_by || 'Unknown',
        initial_payment_recieved_date: log.initial_payment_recieved_date?.seconds
          ? moment(log.initial_payment_recieved_date.seconds * 1000).format('MM/DD/YYYY hh:mm A')
          : null,
        initial_payment: log.initial_payment ? Math.round(parseFloat(log.initial_payment) * 100) / 100 : 0,

        // Employee information - who received additional payment
        additional_payment_recieved_by_id: log.additional_payment_recieved_by_id || null,
        additional_payment_recieved_by: log.additional_payment_recieved_by || null,
        additional_payment_recieved_date: log.additional_payment_recieved_date?.seconds
          ? moment(log.additional_payment_recieved_date.seconds * 1000).format('MM/DD/YYYY hh:mm A')
          : null,
        additional_payment: log.additional_payment ? Math.round(parseFloat(log.additional_payment) * 100) / 100 : 0,

        // Who processed/ended the transaction
        processedBy_id: log.processedBy_id || null,
        processedBy: log.processedBy || 'Unknown',
        end_session_by_id: log.end_session_by_id || null,
        end_session_by: log.end_session_by || null,

        // Discount-related fields for BIR reports
        discount_details: log.discount_details || '',
        discount_applied: log.discount_applied || false,
        discount_percent: log.discount_percent || 0,
        discount_savings: log.discount_savings || 0,
        discounted_amount: log.discounted_amount || amount,
        original_amount: log.original_amount || amount,

        // Refund-related fields
        is_refunded: isRefunded,
        refund_amount: isRefunded ? (log.refund_amount || amount) : 0,
        refunded_by_id: log.refunded_by_id || null,
        refunded_by: log.refunded_by || null,
        status: log.status || (isRefunded ? 'refunded' : 'completed')
      };
    });
  }

  private groupByPaymentType(logs: any[]): any[] {
    const paymentTypes = {};

    logs.forEach(log => {
      if (log.paid) {
        const paymentType = log.payment_type || 'Cash';
        if (!paymentTypes[paymentType]) {
          paymentTypes[paymentType] = { count: 0, amount: 0 };
        }
        paymentTypes[paymentType].count++;

        // Use same amount calculation logic as reports
        let amount = 0;
        if (log.amount_paid !== undefined && log.amount_paid !== null) {
          amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        } else {
          const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
          const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
          amount = initialPayment + additionalPayment;
        }

        paymentTypes[paymentType].amount += Math.round(amount * 100) / 100;
      }
    });

    return Object.entries(paymentTypes).map(([type, data]: [string, any]) => ({
      payment_type: type,
      transaction_count: data.count,
      total_amount: Math.round(data.amount * 100) / 100
    }));
  }

  private groupByVehicleType(logs: any[]): any[] {
    const vehicleTypes = {};

    logs.forEach(log => {
      const vehicleType = log.vehicle_type_custom_name || 'Unknown';
      if (!vehicleTypes[vehicleType]) {
        vehicleTypes[vehicleType] = { count: 0, amount: 0 };
      }
      vehicleTypes[vehicleType].count++;

      if (log.paid) {
        // Use same amount calculation logic as reports
        let amount = 0;
        if (log.amount_paid !== undefined && log.amount_paid !== null) {
          amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        } else {
          const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
          const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
          amount = initialPayment + additionalPayment;
        }

        vehicleTypes[vehicleType].amount += Math.round(amount * 100) / 100;
      }
    });

    return Object.entries(vehicleTypes).map(([type, data]: [string, any]) => ({
      vehicle_type: type,
      transaction_count: data.count,
      total_amount: Math.round(data.amount * 100) / 100
    }));
  }

  private groupByZone(logs: any[]): any[] {
    const zones = {};

    logs.forEach(log => {
      const zoneId = log.zone_id || 'unknown';
      const zoneName = log.zone_name || 'Unknown Zone';

      if (!zones[zoneId]) {
        zones[zoneId] = {
          zone_id: zoneId,
          zone_name: zoneName,
          total_transactions: 0,
          paid_transactions: 0,
          refunded_transactions: 0,
          cancelled_transactions: 0,
          gross_sales: 0,
          refunds: 0,
          discounts: 0,
          net_sales: 0
        };
      }

      zones[zoneId].total_transactions++;

      // Check if this transaction has any payment amount
      const hasPayment = (
        (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) ||
        (log.initial_payment !== undefined && log.initial_payment !== null && parseFloat(log.initial_payment) > 0)
      );

      // Check transaction status
      const isPaid = log.paid === true;
      const isRefunded = log.isRefunded === true || log.status === 'refunded';
      const isCancelled = log.status === 'cancelled' || log.status === 'canceled' || log.isCanceled === true;
      const isCancelledWithPayment = isCancelled && hasPayment;

      // Calculate transaction amount
      let amount = 0;
      if (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) {
        amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
      } else if (isPaid || isRefunded || isCancelledWithPayment) {
        const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
        const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
        amount = initialPayment + additionalPayment;
      }

      // Update counters and amounts - include cancelled with payment in gross sales
      if (isPaid || isRefunded || isCancelledWithPayment) {
        zones[zoneId].paid_transactions++;
        zones[zoneId].gross_sales += amount;
      }

      if (isRefunded) {
        zones[zoneId].refunded_transactions++;
        const refundAmount = log.refund_amount || amount;
        zones[zoneId].refunds += !isNaN(parseFloat(refundAmount)) ? parseFloat(refundAmount) : 0;
      }

      if (isCancelled) {
        zones[zoneId].cancelled_transactions++;
      }

      // Track discounts
      const discountAmount = log.discount_savings || 0;
      zones[zoneId].discounts += !isNaN(parseFloat(discountAmount)) ? parseFloat(discountAmount) : 0;
    });

    // Calculate net sales for each zone and round values
    return Object.values(zones).map((zone: any) => ({
      zone_id: zone.zone_id,
      zone_name: zone.zone_name,
      total_transactions: zone.total_transactions,
      paid_transactions: zone.paid_transactions,
      refunded_transactions: zone.refunded_transactions,
      cancelled_transactions: zone.cancelled_transactions,
      gross_sales: Math.round(zone.gross_sales * 100) / 100,
      refunds: Math.round(zone.refunds * 100) / 100,
      discounts: Math.round(zone.discounts * 100) / 100,
      net_sales: Math.round((zone.gross_sales - zone.refunds - zone.discounts) * 100) / 100
    }));
  }

  private groupByEmployee(logs: any[]): any[] {
    const employees = {};

    logs.forEach(log => {
      // Track by initial payment receiver (primary) or processor (fallback)
      const employeeId = log.initial_payment_recieved_by_id || log.processedBy_id || 'unknown';
      const employeeName = log.initial_payment_recieved_by || log.processedBy || 'Unknown Employee';

      if (!employees[employeeId]) {
        employees[employeeId] = {
          employee_id: employeeId,
          employee_name: employeeName,
          total_transactions: 0,
          transactions_processed: 0,
          transactions_completed: 0,
          initial_payments_received: 0,
          additional_payments_received: 0,
          refunded_transactions: 0,
          cancelled_transactions: 0,
          gross_sales: 0,
          initial_payment_amount: 0,
          additional_payment_amount: 0,
          refunds: 0,
          discounts: 0,
          net_sales: 0
        };
      }

      employees[employeeId].total_transactions++;

      // Check if this transaction has any payment amount
      const hasPayment = (
        (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) ||
        (log.initial_payment !== undefined && log.initial_payment !== null && parseFloat(log.initial_payment) > 0)
      );

      // Check transaction status
      const isPaid = log.paid === true;
      const isRefunded = log.isRefunded === true || log.status === 'refunded';
      const isCancelled = log.status === 'cancelled' || log.status === 'canceled' || log.isCanceled === true;
      const isCancelledWithPayment = isCancelled && hasPayment;

      // Track who processed/started the transaction
      if (log.processedBy_id === employeeId) {
        employees[employeeId].transactions_processed++;
      }

      // Track who completed/ended the transaction
      if (log.time_out && log.end_session_by_id === employeeId) {
        employees[employeeId].transactions_completed++;
      }

      // Track initial payment received by this employee
      if (log.initial_payment_recieved_by_id === employeeId && log.initial_payment) {
        employees[employeeId].initial_payments_received++;
        const initialAmount = !isNaN(parseFloat(log.initial_payment)) ? parseFloat(log.initial_payment) : 0;
        employees[employeeId].initial_payment_amount += initialAmount;
      }

      // Track additional payment received by this employee
      if (log.additional_payment_recieved_by_id === employeeId && log.additional_payment) {
        employees[employeeId].additional_payments_received++;
        const additionalAmount = !isNaN(parseFloat(log.additional_payment)) ? parseFloat(log.additional_payment) : 0;
        employees[employeeId].additional_payment_amount += additionalAmount;
      }

      // Calculate gross sales for transactions where this employee received payment
      // Include: paid, refunded, AND cancelled with payment
      if ((isPaid || isRefunded || isCancelledWithPayment) &&
          (log.initial_payment_recieved_by_id === employeeId || log.additional_payment_recieved_by_id === employeeId)) {
        let amount = 0;
        if (log.amount_paid !== undefined && log.amount_paid !== null && parseFloat(log.amount_paid) > 0) {
          amount = !isNaN(parseFloat(log.amount_paid)) ? parseFloat(log.amount_paid) : 0;
        } else {
          const initialPayment = !isNaN(parseFloat(log.initial_payment || 0)) ? parseFloat(log.initial_payment || 0) : 0;
          const additionalPayment = !isNaN(parseFloat(log.additional_payment || 0)) ? parseFloat(log.additional_payment || 0) : 0;
          amount = initialPayment + additionalPayment;
        }
        employees[employeeId].gross_sales += amount;
      }

      // Track refunds processed by this employee
      if (isRefunded && (log.refunded_by_id === employeeId || log.initial_payment_recieved_by_id === employeeId)) {
        employees[employeeId].refunded_transactions++;
        const refundAmount = log.refund_amount || log.amount_paid || log.initial_payment || 0;
        employees[employeeId].refunds += !isNaN(parseFloat(refundAmount)) ? parseFloat(refundAmount) : 0;
      }

      // Track cancellations by this employee
      if (isCancelled && (log.cancelled_by_id === employeeId || log.initial_payment_recieved_by_id === employeeId)) {
        employees[employeeId].cancelled_transactions++;
      }

      // Track discounts
      if (log.initial_payment_recieved_by_id === employeeId || log.additional_payment_recieved_by_id === employeeId) {
        const discountAmount = log.discount_savings || 0;
        employees[employeeId].discounts += !isNaN(parseFloat(discountAmount)) ? parseFloat(discountAmount) : 0;
      }
    });

    // Calculate net sales and round all values
    return Object.values(employees).map((emp: any) => ({
      employee_id: emp.employee_id,
      employee_name: emp.employee_name,
      total_transactions: emp.total_transactions,
      transactions_processed: emp.transactions_processed,
      transactions_completed: emp.transactions_completed,
      initial_payments_received: emp.initial_payments_received,
      additional_payments_received: emp.additional_payments_received,
      refunded_transactions: emp.refunded_transactions,
      cancelled_transactions: emp.cancelled_transactions,
      gross_sales: Math.round(emp.gross_sales * 100) / 100,
      initial_payment_amount: Math.round(emp.initial_payment_amount * 100) / 100,
      additional_payment_amount: Math.round(emp.additional_payment_amount * 100) / 100,
      refunds: Math.round(emp.refunds * 100) / 100,
      discounts: Math.round(emp.discounts * 100) / 100,
      net_sales: Math.round((emp.gross_sales - emp.refunds - emp.discounts) * 100) / 100
    }));
  }

  private async getHourlyBreakdown(accountKey: string, selectedDate: Date, zone?: string, employee?: string): Promise<HourlyData[]> {
    const hourlyData: HourlyData[] = [];

    for (let hour = 0; hour < 24; hour++) {
      const hourStart = moment(selectedDate).hour(hour).startOf('hour').toDate();
      const hourEnd = moment(selectedDate).hour(hour).endOf('hour').toDate();

      const hourLogs = await this.getLogsForDateRange(accountKey, hourStart, hourEnd, zone, employee);
      const revenue = this.calculateGrossSales(hourLogs);

      hourlyData.push({
        hour: hour,
        time_range: `${moment(hourStart).format('HH:mm')} - ${moment(hourEnd).format('HH:mm')}`,
        transactions: hourLogs.length,
        revenue: revenue,
        average_transaction_value: hourLogs.length > 0 ? revenue / hourLogs.length : 0
      });
    }

    return hourlyData;
  }

  private identifyPeakHours(hourlyData: HourlyData[]): HourlyData[] {
    const avgRevenue = hourlyData.reduce((sum, hour) => sum + hour.revenue, 0) / hourlyData.length;
    return hourlyData.filter(hour => hour.revenue > avgRevenue * 1.5).sort((a, b) => b.revenue - a.revenue);
  }

  private identifyLowTrafficHours(hourlyData: HourlyData[]): HourlyData[] {
    const avgTransactions = hourlyData.reduce((sum, hour) => sum + hour.transactions, 0) / hourlyData.length;
    return hourlyData.filter(hour => hour.transactions < avgTransactions * 0.5).sort((a, b) => a.transactions - b.transactions);
  }

  private calculateTotalCancelledAmount(cancelledTransactions: CancelledTransaction[]): number {
    return cancelledTransactions.reduce((total, transaction) => total + transaction.cancelled_amount, 0);
  }

  private calculateCancellationRate(cancelledTransactions: CancelledTransaction[], totalTransactions: number): number {
    return totalTransactions > 0 ? (cancelledTransactions.length / totalTransactions) * 100 : 0;
  }

  private generateCancellationAuditTrail(cancelledTransactions: CancelledTransaction[]): any {
    return {
      total_cancelled: cancelledTransactions.length,
      cancellation_methods: this.groupCancellationsByMethod(cancelledTransactions),
      approval_status: this.validateCancellationApprovals(cancelledTransactions)
    };
  }

  private groupCancellationsByMethod(cancelledTransactions: CancelledTransaction[]): any {
    const methods = {};
    cancelledTransactions.forEach(transaction => {
      const method = transaction.cancellation_reason || 'Unknown';
      methods[method] = (methods[method] || 0) + 1;
    });
    return methods;
  }

  private validateCancellationApprovals(cancelledTransactions: CancelledTransaction[]): any {
    const approved = cancelledTransactions.filter(t => t.approval_reference).length;
    const pending = cancelledTransactions.length - approved;

    return {
      approved_count: approved,
      pending_approval: pending,
      approval_rate: cancelledTransactions.length > 0 ? (approved / cancelledTransactions.length) * 100 : 0
    };
  }

  private calculateDuration(timeIn: any, timeOut: any): string {
    if (!timeIn) return 'Unknown';
    if (!timeOut) return 'Active';

    const start = moment(timeIn.seconds * 1000);
    const end = moment(timeOut.seconds * 1000);
    const duration = moment.duration(end.diff(start));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  private getTransactionStatus(log: any): string {
    if (log.status === 'cancelled' || log.status === 'canceled') return 'Cancelled';
    if (log.status === 'refunded') return 'Refunded';
    if (log.status === 'completed' && log.paid) return 'Completed';
    if (log.time_out && !log.paid) return 'Unpaid';
    if (!log.time_out) return 'Active';
    return log.status || 'Unknown';
  }

  private async getLifetimeTransactionCount(): Promise<number> {
    // Implementation would query database for total count
    return 0; // Placeholder
  }

  private async getLifetimeRevenue(): Promise<number> {
    // Implementation would query database for total revenue
    return 0; // Placeholder
  }

  private async getLastZReadingInfo(): Promise<any> {
    // Implementation would query last Z reading from database
    return { date: new Date(), number: 'Z-20241220-000001' }; // Placeholder
  }

  private validateSystemIntegrity(): boolean {
    // Implementation would validate system data integrity
    return true; // Placeholder
  }
} 